import {
  managePlayerInWatchlists,
  setDestinationWatchlistIds,
  setManageModalState,
} from 'actions/watchlist-actions'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from 'redux/reducers'
import styles from './watchlist-modal.module.scss'
import genericModalStyles from '../../generic/modalWTN.module.scss'
import { AddOrManagePlayerModalMode } from 'constants/modals'
import { Trans, useTranslation } from 'react-i18next'
import { useCallback, useEffect, useState } from 'react'
import { MAX_PLAYERS_PER_WATCHLIST, MAX_WATCHLISTS_LENGTH } from 'constants/watchlists'
import CheckboxInput from 'components/generic/CheckBoxInput'
import useKeyboardNavigation from 'hooks/use-keyboard-navigation'
import cx from 'classnames'
import Modal from 'react-modal'
import BtnSecondary from 'components/generic/BtnSecondary'
import BtnPrimary from 'components/generic/BtnPrimary'
import { toast } from 'react-toastify'
import WTNSpinner from 'components/generic/WTNSpinner'
import WatchlistNameField, { useWatchlistNameField } from '../common/WatchlistNameField'

const AddOrManageModal = () => {
  const { t: tWatchlists } = useTranslation('strWatchlists')
  const { t: tCommonUI } = useTranslation('strCommonUI')
  const dispatch = useDispatch()
  const { isOpen, mode, player, includedWatchlistsIds } = useSelector(
    (state: AppState) => state.watchlists.manageModal
  )
  const watchlists = useSelector((state: AppState) => state.watchlists.watchlists)
  const allWatchlistsIds = Object.keys(watchlists)
  const [selectedWatchlists, setSelectedWatchlists] = useState<string[]>(includedWatchlistsIds)
  const allWatchlistsLength = allWatchlistsIds.length
  const fullWatchlistsIds = allWatchlistsIds.filter(
    id =>
      player?.id &&
      !watchlists[id].playerIds.includes(player.id) &&
      watchlists[id].playerIds.length >= MAX_PLAYERS_PER_WATCHLIST
  )
  const isProcessing = useSelector((state: AppState) =>
    player?.id ? !!state.watchlists.manageOperations[player.id] : false
  )
  const isFull = allWatchlistsLength >= MAX_WATCHLISTS_LENGTH
  const existingWatchlistsNames = Object.values(watchlists).map(w => w.name)
  const {
    value: watchlistName,
    validate,
    props: nameFieldProps,
  } = useWatchlistNameField({
    forbiddenNames: existingWatchlistsNames,
  })
  const toggleSelection = useCallback(
    (watchlistId: string) => {
      if (selectedWatchlists.includes(watchlistId)) {
        setSelectedWatchlists(selectedWatchlists.filter(wId => wId !== watchlistId))
      } else {
        setSelectedWatchlists(selectedWatchlists.concat(watchlistId))
      }
    },
    [selectedWatchlists]
  )
  const {
    activeIndex,
    setActiveIndex,
    isListContainerFocused,
    listContainerProps,
    listContainerNode,
  } = useKeyboardNavigation<HTMLDivElement, string>({
    listItems: allWatchlistsIds,
    handleSelection: toggleSelection,
    disabledList: fullWatchlistsIds.length ? fullWatchlistsIds : undefined,
  })

  const isAddModal = mode === AddOrManagePlayerModalMode.ADD

  useEffect(() => {
    setSelectedWatchlists(includedWatchlistsIds)
  }, [player, includedWatchlistsIds])

  useEffect(() => {
    if (listContainerNode) listContainerNode.focus()
  }, [listContainerNode])

  if (!player) return null

  const resetState = () => {
    setSelectedWatchlists(includedWatchlistsIds)
    nameFieldProps.onValueChange('')
  }

  const handleCloseModal = () => dispatch(setManageModalState({ isOpen: false }))

  const handleConfirm = () => {
    let isChangeRequested = false
    let createWatchlistName: string | undefined

    if (watchlistName) {
      if (validate(watchlistName)) return
      createWatchlistName = watchlistName
    }

    if (includedWatchlistsIds.length) {
      let removeFromIds: string[] | undefined = []
      const selectedSet = new Set(selectedWatchlists)
      includedWatchlistsIds.forEach(id => {
        if (!selectedSet.delete(id)) removeFromIds?.push(id)
      })

      if (!removeFromIds.length) removeFromIds = undefined
      let addToIds = selectedSet.size ? Array.from(selectedSet) : undefined

      if (createWatchlistName || removeFromIds || addToIds) {
        dispatch(
          managePlayerInWatchlists(player, {
            addToIds: addToIds,
            removeFromIds,
            createWatchlistName,
          })
        )
        isChangeRequested = true
      }
      dispatch(setDestinationWatchlistIds([]))
    } else if (createWatchlistName || selectedWatchlists.length) {
      dispatch(
        managePlayerInWatchlists(
          player,
          {
            addToIds: selectedWatchlists,
            createWatchlistName,
          },
          { addCreatedToDestinationWatchlists: true }
        )
      )
      dispatch(setDestinationWatchlistIds(selectedWatchlists))
      isChangeRequested = true
    }

    if (!isChangeRequested) handleCloseModal()
  }

  return (
    <Modal
      onAfterOpen={() => {
        toast.dismiss()
      }}
      onAfterClose={resetState}
      isOpen={isOpen}
      onRequestClose={handleCloseModal}
      closeTimeoutMS={500}
      className={genericModalStyles.modalWrapper}
      portalClassName='modal-wtn-portal'
      appElement={document.querySelector('main') as HTMLElement}
    >
      <h2 className={cx(genericModalStyles.header, genericModalStyles.reducedMargin)}>
        {isAddModal
          ? tWatchlists('addOrManageModal.add.title')
          : tWatchlists('addOrManageModal.manage.title')}
      </h2>

      <div className={genericModalStyles.body}>
        <p>
          {isAddModal ? (
            <Trans ns='strWatchlists' i18nKey={'addOrManageModal.add.info'}>
              Add <strong className={styles.caps}>{{ name: player.fullName }}</strong> to:
            </Trans>
          ) : (
            <Trans ns='strWatchlists' i18nKey={'addOrManageModal.manage.info'}>
              Assign or remove <strong className={styles.caps}>{{ name: player.fullName }}</strong>{' '}
              to/from:
            </Trans>
          )}
        </p>
        {allWatchlistsLength > 0 && (
          <div
            {...listContainerProps}
            id='my-watchlists'
            role='listbox'
            className={cx(styles.checkboxGroupContainer, {
              '--is-focused': isListContainerFocused,
            })}
            tabIndex={0}
            aria-activedescendant={watchlists[allWatchlistsIds[activeIndex]]?.name}
          >
            {allWatchlistsIds.map((id, i) => {
              const isChecked = selectedWatchlists.includes(id)
              const isFull = fullWatchlistsIds.includes(id)
              const label = watchlists[id].name
              const secondaryLabel = isFull
                ? tWatchlists('addOrManageModal.add.watchlistFull')
                : undefined
              const handleClick = () => {
                toggleSelection(id)
                setActiveIndex(i)
              }
              return (
                <CheckboxInput
                  key={id}
                  classNames={{
                    wrapper: {
                      [styles.checkboxWrapper]: true,
                      '--is-focused': i === activeIndex && isListContainerFocused,
                      '--is-checked': isChecked,
                    },
                    checkbox: styles.checkbox,
                    label: styles.label,
                    secondaryLabel: styles.secondaryLabel,
                  }}
                  onClick={handleClick}
                  label={label}
                  secondaryLabel={secondaryLabel}
                  isChecked={isChecked}
                  asOption
                  ariaSetsize={allWatchlistsLength}
                  ariaSelected={i === activeIndex}
                  isDisabled={isFull}
                />
              )
            })}
          </div>
        )}

        {isFull ? (
          <p role='alert' className={styles.createWatchlistLimitReached}>
            {tWatchlists('addOrManageModal.create.limitReached', {
              count: allWatchlistsLength,
              max: MAX_WATCHLISTS_LENGTH,
            })}
          </p>
        ) : (
          <WatchlistNameField
            {...nameFieldProps}
            label={
              allWatchlistsLength > 0
                ? tWatchlists('addOrManageModal.create.label')
                : tWatchlists('createModal.defaultLabel')
            }
          />
        )}
      </div>
      <div className={cx(genericModalStyles.btns, genericModalStyles['right'])}>
        <BtnSecondary
          onClick={handleCloseModal}
          size={'medium'}
          className={genericModalStyles.button}
        >
          {tCommonUI('button.cancel')}
        </BtnSecondary>

        <BtnPrimary
          onClick={handleConfirm}
          size={'medium'}
          className={cx(genericModalStyles.button, styles.confirmBtn, {
            [styles.processing]: isProcessing,
          })}
          isDisabled={isProcessing || !!nameFieldProps.error}
        >
          {isProcessing ? (
            <WTNSpinner width={25} hideLogo className={styles.spinner} />
          ) : (
            tCommonUI('button.save')
          )}
        </BtnPrimary>
      </div>
    </Modal>
  )
}

export default AddOrManageModal
